import type { BillingCurrencyInformation } from "@/generated";
import { toMoneyNotation } from "@/utils/analytics";

export type BillingCurrencyCode = NonNullable<
  BillingCurrencyInformation["currency"]
>;

export const CURRENCY_SYMBOLS: Record<BillingCurrencyCode, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
};

export const formatMoney = (
  value: number,
  currency: BillingCurrencyCode,
  maximumFractionDigits = 2
) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    currencyDisplay: "symbol",
    maximumFractionDigits,
  })
    .format(value)
    .replace(/ /g, " ");

export const formatCompactMoney = (
  value: number,
  currency: BillingCurrencyCode
) => toMoneyNotation(CURRENCY_SYMBOLS[currency], value);

export function providerCurrency(data?: BillingCurrencyInformation | null) {
  const planCurrency = data?.currency ?? "USD";
  const snapshot = data?.exchange_rate;
  const converted =
    planCurrency !== "USD" &&
    snapshot?.base === "USD" &&
    snapshot.quote === planCurrency &&
    Number.isFinite(snapshot.rate) &&
    snapshot.rate > 0;
  const currency = converted ? planCurrency : "USD";
  const rate = converted ? snapshot.rate : 1;
  const prefix = CURRENCY_SYMBOLS[currency];
  const convert = (value: number) => value * rate;
  const note = converted
    ? undefined
    : planCurrency !== "USD"
      ? "Exchange rate unavailable; provider amounts are shown in USD."
      : "Provider amounts are measured in USD.";
  return {
    currency,
    rate,
    prefix,
    convert,
    toUsd: (value: number) => value / rate,
    note,
    format: (value: number, maximumFractionDigits = 2) =>
      formatMoney(convert(value), currency, maximumFractionDigits),
    compact: (value: number) => formatCompactMoney(convert(value), currency),
  };
}
